import { DragEvent, useEffect, useState } from 'react'
import {
  ArrowUpRight,
  Circle as CircleIcon,
  LineSquiggle,
  Minus,
  Square,
  Triangle,
  Type,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { VisualShape } from 'app/frontend/types/custom/VisualPlan'
import {
  hideSidebarTooltips,
  setSidebarDragData,
  SidebarDragPayload,
} from '../sidebarDrag'

export type Tool = 'select' | 'pencil'

type GeometryType = Exclude<VisualShape['type'], 'icon' | 'text' | 'pencil'>

type Props = {
  tool: Tool
  onToolChange: (tool: Tool) => void
  onAdd: (type: GeometryType) => void
  onAddText: () => void
}

const ShapeToolbar = ({ tool, onToolChange, onAdd, onAddText }: Props) => {
  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    if (!dragging) return
    const clear = () => setDragging(false)
    window.addEventListener('dragend', clear)
    return () => window.removeEventListener('dragend', clear)
  }, [dragging])

  const startDrag = (event: DragEvent, payload: SidebarDragPayload) => {
    setDragging(true)
    hideSidebarTooltips()
    setSidebarDragData(event.dataTransfer, payload)
  }

  const tooltipOpen = dragging ? false : undefined

  return (
    <div className='flex flex-col gap-2'>
      <div className='text-xs font-medium text-muted-foreground'>Shapes</div>
      <div className='grid grid-cols-5 gap-1'>
        <Tooltip open={tooltipOpen} delayDuration={300}>
          <TooltipTrigger asChild>
            <Button
              type='button'
              variant='outline'
              size='icon'
              className='w-full h-9'
              draggable
              onDragStart={(event) =>
                startDrag(event, { kind: 'shape', type: 'rect' })
              }
              onClick={() => onAdd('rect')}
            >
              <Square className='h-4 w-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Rectangle</TooltipContent>
        </Tooltip>
        <Tooltip open={tooltipOpen} delayDuration={300}>
          <TooltipTrigger asChild>
            <Button
              type='button'
              variant='outline'
              size='icon'
              className='w-full h-9'
              draggable
              onDragStart={(event) =>
                startDrag(event, { kind: 'shape', type: 'circle' })
              }
              onClick={() => onAdd('circle')}
            >
              <CircleIcon className='h-4 w-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Circle</TooltipContent>
        </Tooltip>
        <Tooltip open={tooltipOpen} delayDuration={300}>
          <TooltipTrigger asChild>
            <Button
              type='button'
              variant='outline'
              size='icon'
              className='w-full h-9'
              draggable
              onDragStart={(event) =>
                startDrag(event, { kind: 'shape', type: 'triangle' })
              }
              onClick={() => onAdd('triangle')}
            >
              <Triangle className='h-4 w-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Triangle</TooltipContent>
        </Tooltip>
        <Tooltip open={tooltipOpen} delayDuration={300}>
          <TooltipTrigger asChild>
            <Button
              type='button'
              variant='outline'
              size='icon'
              className='w-full h-9'
              draggable
              onDragStart={(event) =>
                startDrag(event, { kind: 'shape', type: 'arrow' })
              }
              onClick={() => onAdd('arrow')}
            >
              <ArrowUpRight className='h-4 w-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Arrow</TooltipContent>
        </Tooltip>
        <Tooltip open={tooltipOpen} delayDuration={300}>
          <TooltipTrigger asChild>
            <Button
              type='button'
              variant='outline'
              size='icon'
              className='w-full h-9'
              draggable
              onDragStart={(event) =>
                startDrag(event, { kind: 'shape', type: 'line' })
              }
              onClick={() => onAdd('line')}
            >
              <Minus className='h-4 w-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Line</TooltipContent>
        </Tooltip>
      </div>
      <div className='grid grid-cols-2 gap-1'>
        <Button
          type='button'
          variant={tool === 'pencil' ? 'default' : 'outline'}
          className='w-full h-9 gap-2'
          onClick={() => onToolChange(tool === 'pencil' ? 'select' : 'pencil')}
        >
          <LineSquiggle className='h-4 w-4' />
          Draw
        </Button>
        <Button
          type='button'
          variant='outline'
          className='w-full h-9 gap-2'
          draggable
          onDragStart={(event) => startDrag(event, { kind: 'text' })}
          onClick={() => onAddText()}
        >
          <Type className='h-4 w-4' />
          Text
        </Button>
      </div>
    </div>
  )
}

export default ShapeToolbar
